/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **The chips that sit beside an artifact's name, on every surface** (`P3a`, S11).
 *
 * > *"Any place we display the name of the artifact, the badge should be near by. It's an implicit
 * > part of the name conceptually."* — Todd, 2026-08-20
 *
 * Pairs with {@link artifactIdentityChips}, which decides *what* to say; this decides how it looks.
 * The split is the same one `readiness.ts` draws: a projection every surface calls, and a component
 * every surface renders, so a list row and a detail header cannot drift.
 *
 * ## Why not `ProvenanceChip`
 *
 * Same **vocabulary and tone** — the colours come from `PROVENANCE_TONE`, so `template` looks like
 * `template` everywhere — but deliberately not the same *scale*. `ProvenanceChip`'s own note says it
 * is small and low-contrast **because it sits on every row of a mapping list and must lose to the
 * value beside it**. This sits beside a name, once, and the requirement is the opposite: *"it must be
 * extremely clear."* Reusing the quiet treatment here would satisfy the letter of one-vocabulary
 * while failing the thing the chip exists to do.
 */

import React from 'react';
import { Box, HStack } from '@chakra-ui/react';

import { PROVENANCE_TONE } from './editors/provenance-presentation';
import { artifactIdentityChips, type IdentifiableArtifact, type ParentNameLookup } from '../../utils/artifact-identity';

const TONE_STYLE = {
  info: { bg: 'info.tint', color: 'info.ink', borderColor: 'info.border' },
  neutral: { bg: 'bg.subtle', color: 'fg.muted', borderColor: 'border.subtle' },
} as const;

export interface ArtifactIdentityProps {
  artifact: IdentifiableArtifact | null | undefined;
  /** Resolves a parent's `objectId` to a name — omit and the chips fall back to unnamed forms. */
  lookupParentName?: ParentNameLookup;
  /** Whether a parent is itself a template, which decides `From` versus `Extends`. */
  parentIsTemplate?: (objectId: string) => boolean;
  /** `sm` beside a list-row name, `md` beside a page or wizard title. */
  size?: 'sm' | 'md';
  /**
   * A catalog preset applied in this editing session — see `artifactIdentityChips`. Session-scoped
   * and never stored, so only a surface that actually knows it passes it.
   */
  appliedTemplateName?: string;
  /** The applied template's documentation link, rendered on its chip (`#1115`). */
  appliedTemplateUrl?: string;
}

export const ArtifactIdentity: React.FC<ArtifactIdentityProps> = ({
  artifact,
  lookupParentName,
  parentIsTemplate,
  size = 'sm',
  appliedTemplateName,
  appliedTemplateUrl,
}) => {
  const chips = artifactIdentityChips(
    artifact,
    lookupParentName,
    parentIsTemplate,
    appliedTemplateName,
    appliedTemplateUrl,
  );
  if (chips.length === 0) return null;

  const scale = size === 'md' ? { fontSize: 'xs', px: 2, py: 0.5 } : { fontSize: '11px', px: 1.5, py: 0 };

  return (
    <HStack gap={1.5} flexShrink={0}>
      {chips.map((chip) => (
        <Box
          key={chip.label}
          // A chip with somewhere to go is a link; one without is a label. Same styling either way,
          // because the chip's job is unchanged — what differs is whether it can be followed.
          as={chip.href ? 'a' : 'span'}
          {...(chip.href
            ? { href: chip.href, target: '_blank', rel: 'noreferrer', title: `Open ${chip.label} documentation` }
            : {})}
          borderWidth="1px"
          borderRadius="full"
          lineHeight="1.5"
          whiteSpace="nowrap"
          fontWeight="500"
          {...(chip.href ? { textDecoration: 'underline', textUnderlineOffset: '2px' } : {})}
          {...scale}
          {...TONE_STYLE[PROVENANCE_TONE[chip.provenance]]}
        >
          {chip.label}
        </Box>
      ))}
    </HStack>
  );
};
